// ClientWorkflowsPanel — universal policy workflow engine UI.
//
// Shows every workflow attached to a client (each LOB = one row).
// For each: progress strip (✓ completed / ⏳ current / ⏸ upcoming /
// — skipped), action buttons (Pause / Resume / Run now / Advance /
// Cancel), and an expandable audit timeline.
//
// Rendered inside the Client Detail modal. Resolves the displayed
// CRM client (a carrier-data-derived display object) to a Supabase
// public.clients row by phone-number match; falls back to a fuzzy
// name match. If no clients row exists, surfaces a one-click
// "Create CRM record" button that inserts a minimal row so the
// workflow engine has something to anchor on.
//
// Reads + writes through:
//   GET  /api/clients/workflows-list?client_id=
//   POST /api/clients/start-workflow
//   POST /api/clients/workflow-action
//   GET  /api/workflows/events-list?workflow_id=
//   GET  /api/carriers/list?line=
//
// Backend lives in api/_workflowDefs.js + api/_workflowRunner.js.

import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { supabase } from '../lib/supabase';

async function apiFetch(path, init = {}) {
  const jwt = readPersistedJwtDirect();
  const res = await fetch(path, {
    ...init,
    headers: { 'content-type': 'application/json', ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}), ...(init.headers || {}) },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
  return body;
}

// ── Constants ────────────────────────────────────────────────
const LINE_OPTIONS = [
  { value: 'health_aca',    label: 'Health (ACA / PPO)' },
  { value: 'medicare',      label: 'Medicare' },
  { value: 'life',          label: 'Life Insurance' },
  { value: 'dental_vision', label: 'Dental + Vision' },
  { value: 'supplemental',  label: 'Supplemental' },
];

const POLICY_TYPES_BY_LINE = {
  health_aca:    ['aca_marketplace','private_ppo','short_term'],
  medicare:      ['medicare_advantage','medicare_supplement','medicare_pdp'],
  life:          ['term','whole','iul','final_expense','mortgage_protection'],
  dental_vision: ['dental_vision'],
  supplemental:  ['hospital_indemnity','accident','critical_illness','cancer','disability'],
};

const POLICY_TYPE_LABELS = {
  aca_marketplace:    'ACA Marketplace',
  private_ppo:        'Private PPO',
  short_term:         'Short-Term Medical',
  medicare_advantage: 'Medicare Advantage (MAPD)',
  medicare_supplement:'Medicare Supplement (Medigap)',
  medicare_pdp:       'Medicare Part D (PDP)',
  term:               'Term Life',
  whole:              'Whole Life',
  iul:                'Indexed Universal Life (IUL)',
  final_expense:      'Final Expense',
  mortgage_protection:'Mortgage Protection',
  dental_vision:      'Dental + Vision',
  hospital_indemnity: 'Hospital Indemnity',
  accident:           'Accident',
  critical_illness:   'Critical Illness',
  cancer:             'Cancer',
  disability:         'Disability',
};

const STATUS_PILL = {
  active:    { bg: 'bg-emerald-100', fg: 'text-emerald-800', label: 'Active' },
  paused:    { bg: 'bg-amber-100',   fg: 'text-amber-800',   label: 'Paused' },
  completed: { bg: 'bg-slate-100',   fg: 'text-slate-700',   label: 'Completed' },
  cancelled: { bg: 'bg-red-100',     fg: 'text-red-800',     label: 'Cancelled' },
};

const LINE_BADGE = {
  health_aca:    { bg: 'bg-sky-100',     fg: 'text-sky-800',     emoji: '🩺' },
  medicare:      { bg: 'bg-emerald-100', fg: 'text-emerald-800', emoji: '🪪' },
  life:          { bg: 'bg-amber-100',   fg: 'text-amber-800',   emoji: '🛡️' },
  dental_vision: { bg: 'bg-cyan-100',    fg: 'text-cyan-800',    emoji: '🦷' },
  supplemental:  { bg: 'bg-violet-100',  fg: 'text-violet-800',  emoji: '➕' },
};

// ── Helpers ──────────────────────────────────────────────────
function normalizePhone(p) {
  return String(p || '').replace(/[^\d]/g, '').slice(-10);
}

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return '—'; }
}

function fmtDateTime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch { return '—'; }
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

// ── Resolve display client → supabase clients row ────────────
function useResolvedClient(clientHint, currentUser) {
  const [state, setState] = React.useState({ loading: true, clientId: null, candidates: [] });
  React.useEffect(() => {
    let alive = true;
    (async () => {
      if (!currentUser?.id) {
        if (alive) setState({ loading: false, clientId: null, candidates: [] });
        return;
      }
      const phone10 = normalizePhone(clientHint?.phone);
      const nameQ   = String(clientHint?.name || '').trim();

      let candidates = [];

      if (phone10) {
        const { data } = await supabase
          .from('clients')
          .select('id, name, phone')
          .ilike('phone', `%${phone10}%`)
          .limit(10);
        candidates = data || [];
      }

      if (candidates.length === 0 && nameQ) {
        // Fall back to a name ilike. Names in the carrier feed are
        // often LAST, FIRST — strip the comma + flip.
        const flipped = nameQ.includes(',')
          ? nameQ.split(',').map(s => s.trim()).reverse().join(' ')
          : nameQ;
        const { data } = await supabase
          .from('clients')
          .select('id, name, phone')
          .ilike('name', `%${flipped}%`)
          .limit(10);
        candidates = data || [];
      }

      if (!alive) return;
      if (candidates.length === 1) {
        setState({ loading: false, clientId: candidates[0].id, candidates });
      } else {
        setState({ loading: false, clientId: null, candidates });
      }
    })();
    return () => { alive = false; };
  }, [clientHint?.phone, clientHint?.name, currentUser?.id]);
  return state;
}

// ── Sub-component: ActivateWorkflowForm ──────────────────────
function ActivateWorkflowForm({ clientId, onCreated, onCancel, showToast }) {
  const [line, setLine]                 = React.useState('medicare');
  const [policyType, setPolicyType]     = React.useState('medicare_advantage');
  const [carrierSlug, setCarrierSlug]   = React.useState('');
  const [carrierName, setCarrierName]   = React.useState('');
  const [carriers, setCarriers]         = React.useState([]);
  const [carriersLoading, setCarriersLoading] = React.useState(false);
  const [policyNumber, setPolicyNumber] = React.useState('');
  const [effectiveDate, setEffectiveDate] = React.useState(todayIso());
  const [renewalDate, setRenewalDate]   = React.useState('');
  const [premiumAmount, setPremiumAmount] = React.useState('');
  const [premiumFreq, setPremiumFreq]   = React.useState('monthly');
  const [submitting, setSubmitting]     = React.useState(false);
  const [error, setError]               = React.useState(null);

  // Load carriers for the selected line.
  React.useEffect(() => {
    let alive = true;
    setCarriersLoading(true);
    apiFetch(`/api/carriers/list?line=${encodeURIComponent(line)}`)
      .then(body => { if (alive) setCarriers(body.carriers || []); })
      .catch(err => { if (alive) setError(err.message); })
      .finally(() => { if (alive) setCarriersLoading(false); });
    return () => { alive = false; };
  }, [line]);

  // Reset policy_type when line changes.
  React.useEffect(() => {
    const allowed = POLICY_TYPES_BY_LINE[line] || [];
    if (!allowed.includes(policyType)) setPolicyType(allowed[0] || '');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [line]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    if (!effectiveDate) { setError('Pick an effective date.'); return; }
    setSubmitting(true);
    try {
      const body = await apiFetch('/api/clients/start-workflow', {
        method: 'POST',
        body: JSON.stringify({
          client_id:        clientId,
          line_of_business: line,
          policy_type:      policyType || null,
          carrier_slug:     carrierSlug || null,
          carrier_name:     carrierName || null,
          policy_number:    policyNumber || null,
          effective_date:   effectiveDate,
          renewal_date:     renewalDate || null,
          premium_amount:   premiumAmount ? Number(premiumAmount) : null,
          premium_frequency: premiumFreq,
        }),
      });
      showToast(`Workflow created — first step "${body.scheduled_step}" scheduled for ${fmtDateTime(body.scheduled_at)}`);
      onCreated(body.workflow_id);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-slate-900">Activate a workflow</h4>
        <button type="button" onClick={onCancel} className="text-xs text-slate-500 hover:text-slate-700">Cancel</button>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <label className="text-sm">
          <span className="text-slate-600 font-medium">Line of business</span>
          <select value={line} onChange={e => setLine(e.target.value)} className="w-full mt-1 border border-slate-300 rounded px-2 py-1.5 text-sm">
            {LINE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
        <label className="text-sm">
          <span className="text-slate-600 font-medium">Policy type</span>
          <select value={policyType} onChange={e => setPolicyType(e.target.value)} className="w-full mt-1 border border-slate-300 rounded px-2 py-1.5 text-sm">
            <option value="">— Not specified —</option>
            {(POLICY_TYPES_BY_LINE[line] || []).map(pt =>
              <option key={pt} value={pt}>{POLICY_TYPE_LABELS[pt] || pt}</option>
            )}
          </select>
        </label>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <label className="text-sm">
          <span className="text-slate-600 font-medium">Carrier (from catalog)</span>
          <select
            value={carrierSlug}
            onChange={e => {
              setCarrierSlug(e.target.value);
              if (e.target.value) setCarrierName('');
            }}
            disabled={carriersLoading}
            className="w-full mt-1 border border-slate-300 rounded px-2 py-1.5 text-sm"
          >
            <option value="">— Pick from catalog —</option>
            {carriers.map(c => <option key={c.id} value={c.slug}>{c.display_name}</option>)}
          </select>
        </label>
        <label className="text-sm">
          <span className="text-slate-600 font-medium">…or carrier name</span>
          <input
            type="text"
            value={carrierName}
            onChange={e => { setCarrierName(e.target.value); if (e.target.value) setCarrierSlug(''); }}
            placeholder="If not in the catalog"
            className="w-full mt-1 border border-slate-300 rounded px-2 py-1.5 text-sm"
          />
        </label>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <label className="text-sm">
          <span className="text-slate-600 font-medium">Policy number</span>
          <input value={policyNumber} onChange={e => setPolicyNumber(e.target.value)} className="w-full mt-1 border border-slate-300 rounded px-2 py-1.5 text-sm font-mono" />
        </label>
        <label className="text-sm">
          <span className="text-slate-600 font-medium">Effective date</span>
          <input type="date" value={effectiveDate} onChange={e => setEffectiveDate(e.target.value)} required className="w-full mt-1 border border-slate-300 rounded px-2 py-1.5 text-sm" />
        </label>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <label className="text-sm">
          <span className="text-slate-600 font-medium">Renewal date</span>
          <input type="date" value={renewalDate} onChange={e => setRenewalDate(e.target.value)} className="w-full mt-1 border border-slate-300 rounded px-2 py-1.5 text-sm" />
        </label>
        <label className="text-sm">
          <span className="text-slate-600 font-medium">Premium</span>
          <input type="number" step="0.01" value={premiumAmount} onChange={e => setPremiumAmount(e.target.value)} className="w-full mt-1 border border-slate-300 rounded px-2 py-1.5 text-sm" />
        </label>
        <label className="text-sm">
          <span className="text-slate-600 font-medium">Frequency</span>
          <select value={premiumFreq} onChange={e => setPremiumFreq(e.target.value)} className="w-full mt-1 border border-slate-300 rounded px-2 py-1.5 text-sm">
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="annual">Annual</option>
            <option value="single_pay">Single pay</option>
          </select>
        </label>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded px-3 py-2 text-sm text-red-800">
          {error}
        </div>
      )}

      <div className="flex justify-end gap-2 pt-1">
        <button type="button" onClick={onCancel} className="text-sm px-3 py-1.5 rounded border border-slate-300 hover:bg-slate-50">Cancel</button>
        <button type="submit" disabled={submitting} className="text-sm px-4 py-1.5 rounded bg-violet-600 text-white font-medium hover:bg-violet-700 disabled:opacity-50">
          {submitting ? 'Activating…' : 'Activate workflow'}
        </button>
      </div>
    </form>
  );
}

// ── Sub-component: Timeline expandable ───────────────────────
function WorkflowTimeline({ workflowId }) {
  const [events, setEvents] = React.useState(null);
  const [error, setError]   = React.useState(null);

  React.useEffect(() => {
    let alive = true;
    apiFetch(`/api/workflows/events-list?workflow_id=${encodeURIComponent(workflowId)}`)
      .then(body => { if (alive) setEvents(body.events || []); })
      .catch(err => { if (alive) setError(err.message); });
    return () => { alive = false; };
  }, [workflowId]);

  if (error) return <div className="text-xs text-red-700 px-3 py-2">Timeline error: {error}</div>;
  if (!events) return <div className="text-xs text-slate-500 px-3 py-2">Loading timeline…</div>;
  if (events.length === 0) return <div className="text-xs text-slate-500 px-3 py-2">No events yet — first step fires when due.</div>;

  return (
    <div className="border-t border-slate-200 mt-2 pt-2 px-3 pb-2 max-h-48 overflow-y-auto">
      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Timeline</div>
      <ol className="space-y-1.5">
        {events.map(ev => (
          <li key={ev.id} className="text-xs text-slate-700 grid grid-cols-[110px_1fr] gap-2">
            <span className="text-slate-500 tabular-nums">{fmtDateTime(ev.occurred_at)}</span>
            <span>
              <span className="font-mono text-slate-900">{ev.event_type}</span>
              {ev.step_key && <span className="text-slate-500"> · {ev.step_key}</span>}
              {ev.channel  && <span className="text-slate-400"> · {ev.channel}</span>}
              {ev.detail?.sms_dryrun && <span className="text-amber-700"> · dry-run</span>}
              {ev.detail?.sms_sid && !ev.detail?.sms_dryrun && <span className="text-emerald-700"> · sent</span>}
              {ev.detail?.error && <span className="text-red-700"> · {ev.detail.error}</span>}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}

// ── Sub-component: WorkflowCard ──────────────────────────────
function WorkflowCard({ workflow, onAction, busy }) {
  const [timelineOpen, setTimelineOpen] = React.useState(false);
  const status = STATUS_PILL[workflow.status] || STATUS_PILL.active;
  const badge  = LINE_BADGE[workflow.line_of_business] || { bg: 'bg-slate-100', fg: 'text-slate-700', emoji: '📋' };

  const canPause   = workflow.status === 'active';
  const canResume  = workflow.status === 'paused';
  const canCancel  = workflow.status === 'active' || workflow.status === 'paused';
  const canAdvance = workflow.status === 'active' || workflow.status === 'paused';
  const canRunNow  = workflow.status === 'active';

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 px-4 py-3 bg-slate-50 border-b border-slate-200">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${badge.bg} ${badge.fg}`}>
              <span>{badge.emoji}</span> {LINE_OPTIONS.find(o => o.value === workflow.line_of_business)?.label || workflow.line_of_business}
            </span>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${status.bg} ${status.fg}`}>
              {status.label}
            </span>
            {workflow.policy_type_label && (
              <span className="text-xs text-slate-600 font-medium">{workflow.policy_type_label}</span>
            )}
          </div>
          <div className="text-sm text-slate-900 mt-1 truncate">
            <span className="font-semibold">{workflow.carrier_name || 'Carrier not set'}</span>
            {workflow.policy_number && <span className="text-slate-500 font-mono ml-2">{workflow.policy_number}</span>}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            Effective {fmtDate(workflow.effective_date)}
            {workflow.next_action_at && workflow.status === 'active' && (
              <> · next step <span className="font-medium text-slate-700">{fmtDateTime(workflow.next_action_at)}</span></>
            )}
            {workflow.completed_at && (
              <> · completed {fmtDate(workflow.completed_at)}</>
            )}
          </div>
          {workflow.paused_reason && workflow.status === 'paused' && (
            <div className="text-xs text-amber-700 mt-0.5 italic">Paused: {workflow.paused_reason}</div>
          )}
        </div>
      </div>

      {/* Progress strip */}
      <div className="p-4 overflow-x-auto">
        <ol className="flex items-stretch gap-1.5 min-w-max">
          {(workflow.steps || []).map((step, idx) => (
            <li
              key={step.key}
              title={`${step.title}${step.scheduled_at ? ' · ' + fmtDate(step.scheduled_at) : ''}${step.skipped_reason ? ' · ' + step.skipped_reason : ''}`}
              className={`flex-shrink-0 px-2 py-1.5 rounded text-xs border ${
                step.state === 'completed' ? 'bg-emerald-50 border-emerald-200 text-emerald-900' :
                step.state === 'current'   ? 'bg-violet-50  border-violet-300  text-violet-900 ring-2 ring-violet-200' :
                step.state === 'skipped'   ? 'bg-slate-50   border-slate-200   text-slate-400 line-through' :
                                             'bg-white      border-slate-200   text-slate-600'
              }`}
              style={{ minWidth: 120 }}
            >
              <div className="flex items-center gap-1">
                <span className="text-[10px] font-mono text-slate-400">{idx + 1}.</span>
                <span className="font-semibold truncate">{step.title}</span>
              </div>
              <div className="text-[10px] text-slate-500 mt-0.5">
                {step.state === 'completed' ? 'Done' :
                 step.state === 'current'   ? 'Now' :
                 step.state === 'skipped'   ? 'Skipped' :
                 step.scheduled_at          ? fmtDate(step.scheduled_at) : ''}
                {step.channel && step.channel !== 'task' && (
                  <span className="ml-1 text-slate-400">· {step.channel}</span>
                )}
              </div>
            </li>
          ))}
        </ol>
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-2 flex-wrap px-4 pb-3">
        {canPause && (
          <button onClick={() => onAction('pause', workflow.id)} disabled={busy} className="text-xs px-2.5 py-1 rounded border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 disabled:opacity-50">
            Pause
          </button>
        )}
        {canResume && (
          <button onClick={() => onAction('resume', workflow.id)} disabled={busy} className="text-xs px-2.5 py-1 rounded border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 disabled:opacity-50">
            Resume
          </button>
        )}
        {canRunNow && (
          <button onClick={() => onAction('run_now', workflow.id)} disabled={busy} className="text-xs px-2.5 py-1 rounded border border-violet-300 bg-violet-50 text-violet-800 hover:bg-violet-100 disabled:opacity-50" title="Fire the next step on the next 5-min tick">
            Run now
          </button>
        )}
        {canAdvance && (
          <button onClick={() => onAction('advance', workflow.id)} disabled={busy} className="text-xs px-2.5 py-1 rounded border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50" title="Mark current step done + move to next">
            Advance →
          </button>
        )}
        {canCancel && (
          <button onClick={() => onAction('cancel', workflow.id)} disabled={busy} className="text-xs px-2.5 py-1 rounded border border-red-300 bg-white text-red-700 hover:bg-red-50 disabled:opacity-50">
            Cancel
          </button>
        )}
        <button onClick={() => setTimelineOpen(o => !o)} className="text-xs px-2.5 py-1 rounded text-slate-600 hover:bg-slate-100 ml-auto">
          {timelineOpen ? 'Hide timeline' : 'View timeline'}
        </button>
      </div>

      {timelineOpen && <WorkflowTimeline workflowId={workflow.id} />}
    </div>
  );
}

// ── Sub-component: NoClientRecord prompt ─────────────────────
function NoClientRecord({ clientHint, candidates, currentUser, onCreated, showToast }) {
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [selected, setSelected] = React.useState(null);

  async function createRow() {
    if (!currentUser?.id) return;
    setBusy(true); setError(null);
    try {
      const { data, error: insErr } = await supabase
        .from('clients')
        .insert({
          owner_id: currentUser.id,
          name:     clientHint?.name || 'Unnamed client',
          phone:    clientHint?.phone || null,
          email:    clientHint?.email || null,
        })
        .select('id, name')
        .single();
      if (insErr) throw insErr;
      showToast(`CRM client record created — workflows are now available.`);
      onCreated(data.id);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
      <h4 className="font-semibold text-amber-900 text-sm mb-1">Workflow engine needs a Clients table record</h4>
      <p className="text-xs text-amber-900 leading-relaxed">
        Universal workflows attach to the <code className="bg-amber-100 px-1 rounded">public.clients</code> table.
        {candidates && candidates.length > 1
          ? ' Multiple candidates matched this profile — pick one or create a new record.'
          : ' This profile doesn\'t have a matching Clients row yet.'}
      </p>

      {candidates && candidates.length > 0 && (
        <div className="mt-2 space-y-1">
          {candidates.map(c => (
            <button
              key={c.id}
              onClick={() => { setSelected(c.id); onCreated(c.id); }}
              className={`block w-full text-left text-xs px-2 py-1.5 rounded border ${selected === c.id ? 'border-violet-400 bg-violet-50' : 'border-amber-200 bg-white hover:bg-amber-50'}`}
            >
              Use existing: <span className="font-semibold">{c.name}</span> <span className="text-slate-500">· {c.phone || 'no phone'}</span>
            </button>
          ))}
        </div>
      )}

      {error && <div className="text-xs text-red-700 mt-2">Error: {error}</div>}

      <div className="mt-2 flex items-center gap-2">
        <button
          onClick={createRow}
          disabled={busy}
          className="text-xs bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white font-semibold px-3 py-1.5 rounded"
        >
          {busy ? 'Creating…' : 'Create Clients row for this profile'}
        </button>
      </div>
    </div>
  );
}

// ── Sub-component: AgentNotesEditor ──────────────────────────
// Inline textarea that writes directly to clients.agent_notes. The
// renderer pulls the first ~110 chars (sentence-boundary aware) and
// drops it into welcome + check-in templates so each client gets
// personalized opening lines. Saves on blur — no save button —
// because operators usually skim, type, click elsewhere.
function AgentNotesEditor({ clientId, showToast }) {
  const [value, setValue]   = React.useState('');
  const [loaded, setLoaded] = React.useState(false);
  const [busy, setBusy]     = React.useState(false);
  const [dirty, setDirty]   = React.useState(false);
  const [err, setErr]       = React.useState(null);

  // Load on mount.
  React.useEffect(() => {
    let alive = true;
    (async () => {
      if (!clientId) return;
      const { data, error } = await supabase
        .from('clients')
        .select('agent_notes')
        .eq('id', clientId)
        .maybeSingle();
      if (!alive) return;
      if (error) {
        // If the column doesn't exist yet (migration 100 not run),
        // surface a hint rather than a cryptic Postgres error.
        if (/column.*agent_notes.*does not exist/i.test(error.message)) {
          setErr('Column not yet migrated — run migration 100 to enable AI personalization notes.');
        } else {
          setErr(error.message);
        }
      } else {
        setValue(data?.agent_notes || '');
      }
      setLoaded(true);
    })();
    return () => { alive = false; };
  }, [clientId]);

  async function save() {
    if (!dirty || !clientId) return;
    setBusy(true); setErr(null);
    const { error } = await supabase
      .from('clients')
      .update({ agent_notes: value || null })
      .eq('id', clientId);
    if (error) {
      setErr(error.message);
      showToast(`Save failed: ${error.message}`, 'error');
    } else {
      setDirty(false);
      showToast('Notes saved — will personalize future AI messages.');
    }
    setBusy(false);
  }

  if (!loaded) return null;
  if (err && /column.*agent_notes/i.test(err)) {
    // Surface the migration hint as a soft notice — don't block the rest of the panel.
    return (
      <div className="mb-3 bg-amber-50 border border-amber-200 rounded px-3 py-2 text-xs text-amber-900">
        <strong>AI personalization notes:</strong> {err}
      </div>
    );
  }

  return (
    <div className="mb-3 bg-violet-50 border border-violet-200 rounded-lg p-3">
      <label className="block text-xs font-semibold text-violet-900 mb-1">
        AI personalization notes <span className="font-normal text-violet-700">— captures retire status, family, hobbies, comms prefs</span>
      </label>
      <textarea
        value={value}
        onChange={e => { setValue(e.target.value); setDirty(true); }}
        onBlur={save}
        placeholder={'e.g. "Just retired. Loves golf. Wife turning 65 in October. Prefers phone calls after 3 PM."'}
        rows={2}
        className="w-full bg-white border border-violet-300 rounded px-2 py-1.5 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-300"
      />
      <div className="flex items-center justify-between mt-1 text-[11px]">
        <span className="text-violet-700">
          {dirty ? 'Unsaved — click outside the box to save' : busy ? 'Saving…' : value ? `${value.length} chars · saved` : 'Empty — messages will be generic'}
        </span>
        {err && !/column.*agent_notes/i.test(err) && <span className="text-red-700">{err}</span>}
      </div>
    </div>
  );
}

// ── Main exported panel ──────────────────────────────────────
export default function ClientWorkflowsPanel({ clientHint, currentUser, showToast = () => {} }) {
  const { loading: resolving, clientId: resolvedId, candidates } = useResolvedClient(clientHint, currentUser);
  const [manualId, setManualId] = React.useState(null);
  const clientId = manualId || resolvedId;

  const [data, setData]   = React.useState(null);
  const [error, setError] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [showActivate, setShowActivate] = React.useState(false);
  const [busy, setBusy]   = React.useState(false);

  const refresh = React.useCallback(async () => {
    if (!clientId) return;
    setLoading(true); setError(null);
    try {
      const body = await apiFetch(`/api/clients/workflows-list?client_id=${encodeURIComponent(clientId)}`);
      setData(body);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  React.useEffect(() => { refresh(); }, [refresh]);

  async function handleAction(action, workflowId) {
    if (!action || !workflowId) return;
    let reason = null;
    if (action === 'pause' || action === 'cancel') {
      reason = window.prompt(`Reason for ${action} (optional):`) || null;
    }
    if (action === 'cancel') {
      const ok = await (window.hqConfirm ? window.hqConfirm({
        title: 'Cancel this workflow?',
        body: 'This is terminal — it cannot be resumed.',
        confirmLabel: 'Cancel workflow', danger: true,
      }) : Promise.resolve(window.confirm('Cancel this workflow? This is terminal — it cannot be resumed.')));
      if (!ok) return;
    }
    setBusy(true);
    try {
      await apiFetch('/api/clients/workflow-action', {
        method: 'POST',
        body: JSON.stringify({ workflow_id: workflowId, action, reason }),
      });
      showToast(`Workflow ${action === 'run_now' ? 'queued for next tick' : action + 'd'}.`);
      await refresh();
    } catch (err) {
      showToast(`Workflow action failed: ${err.message}`, 'error');
    } finally {
      setBusy(false);
    }
  }

  if (resolving) {
    return (
      <div className="border-t border-slate-200 bg-white p-4">
        <div className="text-xs text-slate-500">Looking up CRM client record…</div>
      </div>
    );
  }

  if (!clientId) {
    return (
      <div className="border-t border-slate-200 bg-white p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-slate-900">Policy workflows</h3>
        </div>
        <NoClientRecord
          clientHint={clientHint}
          candidates={candidates}
          currentUser={currentUser}
          onCreated={(id) => setManualId(id)}
          showToast={showToast}
        />
      </div>
    );
  }

  return (
    <div className="border-t border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h3 className="font-semibold text-slate-900">Policy workflows</h3>
          <p className="text-xs text-slate-500">AI-driven post-sale lifecycle per policy. ACA / Medicare / Life / Dental + Vision / Supplemental.</p>
        </div>
        {!showActivate && (
          <button
            onClick={() => setShowActivate(true)}
            className="text-sm bg-violet-600 hover:bg-violet-700 text-white font-semibold px-3 py-1.5 rounded"
          >
            + Activate workflow
          </button>
        )}
      </div>

      <AgentNotesEditor clientId={clientId} showToast={showToast} />

      {showActivate && (
        <div className="mb-3">
          <ActivateWorkflowForm
            clientId={clientId}
            showToast={showToast}
            onCancel={() => setShowActivate(false)}
            onCreated={() => { setShowActivate(false); refresh(); }}
          />
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded px-3 py-2 text-sm text-red-800 mb-3">
          Couldn't load workflows: {error}
        </div>
      )}

      {loading && !data && (
        <div className="text-xs text-slate-500">Loading workflows…</div>
      )}

      {data && data.workflows.length === 0 && !showActivate && (
        <div className="bg-slate-50 border border-slate-200 rounded p-4 text-sm text-slate-600 text-center">
          No workflows yet. Click <span className="font-semibold">Activate workflow</span> to start one for this client.
        </div>
      )}

      <div className="space-y-3">
        {(data?.workflows || []).map(wf => (
          <WorkflowCard
            key={wf.id}
            workflow={wf}
            onAction={handleAction}
            busy={busy}
          />
        ))}
      </div>
    </div>
  );
}
